/**
 * Provider migration — TRANSLATING THE SERVER'S REFUSALS.
 *
 * ── THE PROBLEM THIS SOLVES ────────────────────────────────────────────────
 * The backend refuses with a governed, fully structured 409:
 *
 *   { error: { code: 'PROVIDER_MIGRATION_FINALIZATION_REFUSED',
 *              gate: 'ASSET_NOT_ON_TARGET_PROVIDER',
 *              message: 'Finalization refused at gate …: <asset> on <provider>' } }
 *
 * The panels used to render `err.message`, which for an `AxiosError` is the
 * generic transport string "Request failed with status code 409". Every one of
 * the twenty distinct gates therefore looked identical on screen, and the whole
 * diagnostic payload the server had gone to the trouble of producing was
 * discarded by the client.
 *
 * ── THE TWO RULES ──────────────────────────────────────────────────────────
 * 1. NEVER HIDE THE SERVER'S REASON. The gate name and the server's own
 *    sentence are always carried through to `detail`, alongside the plain-English
 *    `headline`. The explanation supplements the refusal; it never replaces it.
 *
 * 2. NEVER GUESS. A refusal is recognised by its `gate`/`code` FIELDS, never by
 *    its HTTP status. An unrecognised 409 keeps the server's own message and is
 *    reported as an unrecognised refusal — it is never coerced into the
 *    ownership case just because both happen to be 409s. `nextAction` is
 *    non-null only for gates whose remedy is actually known.
 */

/** What the operator should do next, when the gate makes that unambiguous. */
export type RefusalNextAction = 'OWNERSHIP' | 'RUN' | 'APPROVE' | 'RELOAD' | null;

export interface ParsedMigrationError {
  /** HTTP status, when the failure came from a response at all. */
  status: number | null;
  /** e.g. `PROVIDER_MIGRATION_FINALIZATION_REFUSED`. Null if not structured. */
  code: string | null;
  /** e.g. `ASSET_NOT_ON_TARGET_PROVIDER`. Only finalization refusals carry one. */
  gate: string | null;
  /** The `reason` string carried by run and ownership refusals. */
  reason: string | null;
  /** The server's own sentence, verbatim. Never discarded. */
  serverMessage: string | null;
  /** Plain-English explanation. Falls back to the server message, then the raw error. */
  headline: string;
  /** The server's own words, shown beneath the headline. Null if identical. */
  detail: string | null;
  nextAction: RefusalNextAction;
  /** True when the server refused for a reason this module recognises. */
  recognised: boolean;
}

/**
 * Plain-English readings of the twenty finalization gates.
 *
 * Each says what is WRONG, in the operator's terms. None of them says the
 * operation succeeded, and none softens a refusal into a warning.
 */
const GATE_EXPLANATIONS: Record<string, { headline: string; nextAction: RefusalNextAction }> = {
  ASSET_NOT_ON_TARGET_PROVIDER: {
    headline: 'Ownership has not been moved to the target provider yet.',
    nextAction: 'OWNERSHIP',
  },
  ASSET_NOT_ACTIVE: {
    headline:
      'One of the migrated assets is no longer active — it was deleted or quarantined after the copy.',
    nextAction: null,
  },
  STATUS_NOT_VERIFYING: {
    headline:
      'This migration is no longer waiting to be finalized; its status changed. Reload to see the current state.',
    nextAction: 'RELOAD',
  },
  NOT_APPROVED: {
    headline: 'This migration has not been approved. Approve the dry run first.',
    nextAction: 'APPROVE',
  },
  MIGRATION_NOT_FOUND: {
    headline: 'This migration no longer exists.',
    nextAction: 'RELOAD',
  },
  UNEXPECTED_MIGRATION_COUNT: {
    headline:
      'Another migration is still in flight. Only one migration may be active at a time — finish or retire the other one first.',
    nextAction: null,
  },
  TOTAL_OBJECTS_MISMATCH: {
    headline: "The migration's recorded object count no longer matches its items.",
    nextAction: null,
  },
  ITEM_COUNT_MISMATCH: {
    headline: 'This migration has no items to finalize.',
    nextAction: null,
  },
  ITEM_NOT_COMPLETED: {
    headline: 'Not every object finished copying. Finalization requires all of them.',
    nextAction: 'RUN',
  },
  ITEM_NOT_VERIFIED: {
    headline: 'Not every copied object has been checksum-verified.',
    nextAction: 'RUN',
  },
  ITEM_CHECKSUM_MISSING: {
    headline: 'An object has no recorded checksum, so its copy cannot be proven.',
    nextAction: 'RUN',
  },
  ITEM_CHECKSUM_UNEQUAL: {
    headline: 'A source and destination checksum disagree. The copy is not trustworthy.',
    nextAction: null,
  },
  DESTINATION_OBJECT_EXISTS_DIFFERENT: {
    headline:
      'The destination already contains an object with different content. It was NOT overwritten.',
    nextAction: null,
  },
  TARGET_OBJECT_MISSING: {
    headline: 'An object is missing from the target provider. It cannot be finalized.',
    nextAction: null,
  },
  TARGET_CHECKSUM_MISMATCH: {
    headline:
      'An object on the target provider no longer matches the checksum recorded when it was copied.',
    nextAction: null,
  },
  SOURCE_OBJECT_MISSING: {
    headline:
      'A source object is missing. Source copies must remain retained — a migration that lost its source cannot be completed.',
    nextAction: null,
  },
  PROTECTED_ASSET_IN_MIGRATION: {
    headline: 'A protected (non-active) asset is present in this migration and must not be moved.',
    nextAction: null,
  },
  PROTECTED_ASSET_ON_TARGET: {
    headline:
      "A protected asset's object was found on the target provider. It should not be there.",
    nextAction: null,
  },
  DIVERGENT_ASSET_VERSION: {
    headline: 'An asset version points at a different storage key than its asset.',
    nextAction: null,
  },
  UNSUPPORTED_DRIVER: {
    headline: 'One of the two providers has no registered storage driver in this build.',
    nextAction: null,
  },
  STATUS_WRITE_ROW_COUNT_NOT_ONE: {
    headline:
      "The migration's status changed underneath this request. Reload and check before retrying.",
    nextAction: 'RELOAD',
  },
};

/**
 * Refusals from the run/approve and ownership endpoints. These carry a free-text
 * `reason` rather than a gate, so they are matched on a distinctive fragment of
 * that reason — anchored to wording the server actually produces.
 */
const REASON_PATTERNS: Array<{
  test: RegExp;
  headline: string;
  nextAction: RefusalNextAction;
}> = [
  {
    test: /requires status READY or PAUSED, found RUNNING/i,
    headline: 'This migration is already running. Wait for the current run to finish.',
    nextAction: 'RELOAD',
  },
  {
    test: /requires status READY or PAUSED/i,
    headline: 'This migration is not in a state that can be run.',
    nextAction: 'RELOAD',
  },
  {
    test: /approval requires status DRY_RUN_COMPLETE/i,
    headline: 'This migration has already been approved, or is not ready for approval.',
    nextAction: 'RELOAD',
  },
  {
    test: /ownership transition requires status VERIFYING/i,
    headline:
      'Ownership can only be moved after the copy has finished and before the migration is finalized.',
    nextAction: 'RELOAD',
  },
  {
    test: /has not been approved/i,
    headline: 'This migration has not been approved yet.',
    nextAction: 'APPROVE',
  },
  {
    test: /claimed by another run request/i,
    headline:
      'Another run is already active for this migration. This request copied nothing — watch the live progress instead of starting a second run.',
    nextAction: 'RELOAD',
  },
  {
    test: /DESTINATION_OBJECT_EXISTS_DIFFERENT/i,
    headline:
      'The destination already contains an object with different content. It was NOT overwritten.',
    nextAction: null,
  },
  {
    test: /migration is terminal/i,
    headline: 'This migration has already finished and cannot be changed.',
    nextAction: 'RELOAD',
  },
  {
    test: /D-8 violation/i,
    headline: 'A protected (non-active) asset is present in this migration and must not be moved.',
    nextAction: null,
  },
  {
    test: /snapshot mismatch/i,
    headline: "The migration's recorded object count no longer matches its items.",
    nextAction: null,
  },
  {
    test: /unsupported driver/i,
    headline: 'One of the two providers has no registered storage driver in this build.',
    nextAction: null,
  },
];

interface ErrorEnvelope {
  code?: unknown;
  gate?: unknown;
  reason?: unknown;
  message?: unknown;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/**
 * Pull the structured envelope out of whatever was thrown.
 *
 * `platformApiClient` rejects non-401 failures with the original `AxiosError`,
 * which is an `Error` and still carries `.response.data`. Anything else — a
 * plain `Error`, a string, a network failure with no response — degrades to the
 * raw message rather than being invented.
 */
function readEnvelope(err: unknown): {
  status: number | null;
  body: ErrorEnvelope | null;
  rawMessage: string;
} {
  const rawMessage =
    err instanceof Error && err.message.length > 0
      ? err.message
      : typeof err === 'string' && err.length > 0
        ? err
        : 'The server refused the operation.';

  if (typeof err !== 'object' || err === null) return { status: null, body: null, rawMessage };

  const response = (err as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) {
    return { status: null, body: null, rawMessage };
  }

  const status = (response as { status?: unknown }).status;
  const data = (response as { data?: unknown }).data;
  const errorObj =
    typeof data === 'object' && data !== null ? (data as { error?: unknown }).error : undefined;

  return {
    status: typeof status === 'number' ? status : null,
    body: typeof errorObj === 'object' && errorObj !== null ? (errorObj as ErrorEnvelope) : null,
    rawMessage,
  };
}

/**
 * Translate a thrown migration error into something an operator can act on,
 * WITHOUT ever discarding what the server said.
 */
export function parseMigrationError(err: unknown): ParsedMigrationError {
  const { status, body, rawMessage } = readEnvelope(err);

  const code = body ? str(body.code) : null;
  const gate = body ? str(body.gate) : null;
  const reason = body ? str(body.reason) : null;
  const serverMessage = body ? str(body.message) : null;

  // ── a recognised finalization gate ───────────────────────────────────────
  // Matched on the GATE FIELD only. A 409 with an unknown or absent gate never
  // reaches this branch, so an unrelated conflict can never be mislabelled as
  // the ownership case.
  const byGate = gate !== null ? GATE_EXPLANATIONS[gate] : undefined;
  if (byGate) {
    return {
      status,
      code,
      gate,
      reason,
      serverMessage,
      headline: byGate.headline,
      detail: serverMessage,
      nextAction: byGate.nextAction,
      recognised: true,
    };
  }

  // ── a recognised run/ownership refusal, matched on its reason text ───────
  const reasonText = reason ?? serverMessage;
  if (reasonText !== null) {
    const hit = REASON_PATTERNS.find((p) => p.test.test(reasonText));
    if (hit) {
      return {
        status,
        code,
        gate,
        reason,
        serverMessage,
        headline: hit.headline,
        detail: serverMessage,
        nextAction: hit.nextAction,
        recognised: true,
      };
    }
  }

  // ── structured, but not a refusal this module knows ──────────────────────
  // The server's own message is promoted to the headline rather than being
  // replaced by a guess, and `recognised` stays false so the UI can say plainly
  // that this is an unexpected refusal.
  if (serverMessage !== null) {
    return {
      status,
      code,
      gate,
      reason,
      serverMessage,
      headline: serverMessage,
      detail: gate !== null ? `Gate: ${gate}` : code !== null ? `Code: ${code}` : null,
      nextAction: null,
      recognised: false,
    };
  }

  // ── not structured at all ────────────────────────────────────────────────
  return {
    status,
    code,
    gate,
    reason,
    serverMessage,
    headline: rawMessage,
    detail: null,
    nextAction: null,
    recognised: false,
  };
}
